import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Table } from 'bootstrap-4-react/lib/components';
import { MenuList } from '../../common/MenuList';
import { Dropdown } from 'react-bootstrap';
import BatchPagination from '../../batch/BatchPagination';
import { numOfPage } from '../../../constant';
import { computeAutoSelectedSellers, getSelectList, renderTableRow } from '../../../config/utils';
import { fetchCompanyFilters, getFilterSellers } from '../../../store/actions/rateCard';
import { getAllSellersList } from '../../../store/actions';
import { InvitationStatusPill } from '../utils/invitationStatus';
import mapLodash from 'lodash/map';
import moment from 'moment';

// ============================================================
// Column configurations
// ============================================================

// Per-invitation view (regular user, or super-admin with legacy shape)
export const COLUMNS_CONFIG = [
    { key: 'CompanySellerName', label: 'Company Seller Name', sortable: true },
    { key: 'Email', label: 'Email', sortable: true },
    { key: 'CreatedDate', label: 'Created At', sortable: true },
    { key: 'Status', label: 'Status', sortable: true },
    { key: 'InvitationUrl', label: 'URL', sortable: false },
];

// Aggregate view (super admin): one row per CompanySeller with referralCount.
// Company Name / Seller Name are omitted because CompanySellerName already
// carries the "Company/Seller" caption.
export const AGGREGATE_COLUMNS_CONFIG = [
    { key: 'CompanySellerName', label: 'Company Seller Name', sortable: true },
    { key: 'ReferralCount', label: 'Referral Count', sortable: true },
];

// Subcomponent: Page Header
export const AffiliatePageHeader = ({ onAddClick, onBackClick, onFilterClick, showFilterButton, showAddButton = true }) => (
    <div className="main__tile_heading cus-btn-include-sec">
        Partner Management
        <div className="top_right_button_container">
            {showFilterButton && (
                <Button
                    className="cus-seconday-bg-btn top-btn FilterButton"
                    onClick={onFilterClick}
                >
                    Filter
                </Button>
            )}
            {showAddButton && (
                <Button className="cus-primary-bg-btn" onClick={onAddClick}>
                    Add Company/Seller
                </Button>
            )}
            {onBackClick && (
                <Button className="cus-primary-transparent-btn" onClick={onBackClick}>
                    Back
                </Button>
            )}
        </div>
    </div>
);

AffiliatePageHeader.propTypes = {
    onAddClick: PropTypes.func.isRequired,
    onBackClick: PropTypes.func,
    onFilterClick: PropTypes.func,
    showFilterButton: PropTypes.bool,
    showAddButton: PropTypes.bool,
};

// ============================================================
// Sortable table header (shared)
// ============================================================

const SortableHeader = ({ columns, sortColumn, sortOrder, onSort, trailingBlank }) => (
    <thead border="Green">
        <tr className="trow">
            <th className="trows-s">#</th>
            {columns.map(column => (
                <th key={column.key} className={column.key === 'Status' ? 'trows-s' : ''}>
                        {column.sortable ? (
                            <button
                                type="button"
                                className={sortColumn === column.key ? 'active sortBtn' : 'sortColumn sortBtn'}
                                onClick={e => onSort(e, column.key)}
                            >
                                {column.label}{' '}
                                <i
                                    className={
                                        sortColumn === column.key && sortOrder === 'ASCENDING'
                                            ? 'fa fa-arrow-up'
                                            : 'fa fa-arrow-down'
                                    }
                                    aria-hidden="true"
                                ></i>
                            </button>
                        ) : (
                            column.label
                        )}
                    </th>
            ))}
            {trailingBlank && <th className="trows-s"></th>}
        </tr>
    </thead>
);

SortableHeader.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    sortColumn: PropTypes.string,
    sortOrder: PropTypes.string.isRequired,
    onSort: PropTypes.func.isRequired,
    trailingBlank: PropTypes.bool,
};

// Backward-compatible export used by existing tests / consumers
export const AffiliateTableHeader = ({ sortColumn, sortOrder, onSort }) => (
    <SortableHeader
        columns={COLUMNS_CONFIG}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onSort={onSort}
        trailingBlank
    />
);

AffiliateTableHeader.propTypes = {
    sortColumn: PropTypes.string,
    sortOrder: PropTypes.string.isRequired,
    onSort: PropTypes.func.isRequired,
};

// ============================================================
// Per-invitation table (regular user view — unchanged shape)
// ============================================================

export const AffiliateTableRow = ({ item, index, startIdx, onDelete, onCopy }) => (
    <tr key={item.Id}>
        <td className="trows-s">{startIdx + index + 1}</td>
        <td className="trows-s">{item.CompanySellerName || 'N/A'}</td>
        <td className="trows-s">{item.Email || 'N/A'}</td>
        <td className="trows-s">{item.CreatedDate ? moment(item.CreatedDate).format('MM-DD-YYYY HH:mm') : 'N/A'}</td>
        <td className="trows-s">
            {item.Status ? <InvitationStatusPill status={item.Status} /> : 'N/A'}
        </td>
        <td className="trows-s">
            {item.InvitationUrl ? (
                <div className="keyFieldWrapper">
                    <input
                        value={item.InvitationUrl}
                        className="form-control"
                        readOnly
                    />
                    <button
                        type="button"
                        onClick={(e) => onCopy(e, item)}
                        className="btn copyBtn"
                        title="Copy URL"
                    >
                        <i className="fa fa-clone" aria-hidden="true"></i>
                    </button>
                </div>
            ) : (
                'N/A'
            )}
        </td>
        <td>
            <MenuList>
                <Dropdown.Item className="printLabelBtn" onClick={() => onDelete(item.Id)}>
                    Delete
                </Dropdown.Item>
            </MenuList>
        </td>
    </tr>
);

AffiliateTableRow.propTypes = {
    item: PropTypes.shape({
        Id: PropTypes.number.isRequired,
        CompanySellerName: PropTypes.string,
        Email: PropTypes.string,
        CreatedDate: PropTypes.string,
        Status: PropTypes.string,
        InvitationUrl: PropTypes.string,
        InviteToken: PropTypes.string,
    }).isRequired,
    index: PropTypes.number.isRequired,
    startIdx: PropTypes.number.isRequired,
    onDelete: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
};

export const AffiliateTableBody = ({ data, startIdx, onDelete, onCopy }) =>
    data && data.length > 0 ? (
        <tbody>
            {data.map((item, index) => (
                <AffiliateTableRow key={item.Id} item={item} index={index} startIdx={startIdx} onDelete={onDelete} onCopy={onCopy} />
            ))}
        </tbody>
    ) : (
        <tbody>{renderTableRow(8, 'No Record Found')}</tbody>
    );

AffiliateTableBody.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    startIdx: PropTypes.number.isRequired,
    onDelete: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
};

export const AffiliateTable = ({ data, sortColumn, sortOrder, onSort, startIdx, onDelete, onCopy }) => (
    <Table className="table affiliateTable keyGenerate">
        <AffiliateTableHeader sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} />
        <AffiliateTableBody data={data} startIdx={startIdx} onDelete={onDelete} onCopy={onCopy} />
    </Table>
);

AffiliateTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    sortColumn: PropTypes.string,
    sortOrder: PropTypes.string.isRequired,
    onSort: PropTypes.func.isRequired,
    startIdx: PropTypes.number.isRequired,
    onDelete: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
};

// ============================================================
// Aggregate table (super admin): one row per CompanySeller
// ============================================================

export const AggregateTableRow = ({ item, index, startIdx, onReferralCountClick }) => (
    <tr key={item.CompanySellerId ?? `${item.CompanySellerName}-${index}`}>
        <td className="trows-s">{startIdx + index + 1}</td>
        <td className="trows-s">{item.CompanySellerName || 'N/A'}</td>
        <td className="trows-s">
            {item.ReferralCount > 0 ? (
                <button
                    type="button"
                    className="btn btn-link p-0 referralCountBadge"
                    onClick={() => onReferralCountClick(item)}
                    title="View referred users"
                >
                    <span className="badge badge-primary">{item.ReferralCount}</span>
                </button>
            ) : (
                <span>{item.ReferralCount ?? 0}</span>
            )}
        </td>
    </tr>
);

AggregateTableRow.propTypes = {
    item: PropTypes.shape({
        CompanySellerId: PropTypes.number,
        CompanySellerName: PropTypes.string,
        ReferralCount: PropTypes.number,
    }).isRequired,
    index: PropTypes.number.isRequired,
    startIdx: PropTypes.number.isRequired,
    onReferralCountClick: PropTypes.func.isRequired,
};

export const AggregateTableBody = ({ data, startIdx, onReferralCountClick }) =>
    data && data.length > 0 ? (
        <tbody>
            {data.map((item, index) => (
                <AggregateTableRow
                    key={item.CompanySellerId ?? `${item.CompanySellerName}-${index}`}
                    item={item}
                    index={index}
                    startIdx={startIdx}
                    onReferralCountClick={onReferralCountClick}
                />
            ))}
        </tbody>
    ) : (
        <tbody>{renderTableRow(3, 'No Record Found')}</tbody>
    );

AggregateTableBody.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    startIdx: PropTypes.number.isRequired,
    onReferralCountClick: PropTypes.func.isRequired,
};

export const AggregateAffiliateTable = ({ data, sortColumn, sortOrder, onSort, startIdx, onReferralCountClick }) => (
    <Table className="table affiliateTable keyGenerate">
        <SortableHeader
            columns={AGGREGATE_COLUMNS_CONFIG}
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            onSort={onSort}
            trailingBlank={false}
        />
        <AggregateTableBody data={data} startIdx={startIdx} onReferralCountClick={onReferralCountClick} />
    </Table>
);

AggregateAffiliateTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    sortColumn: PropTypes.string,
    sortOrder: PropTypes.string.isRequired,
    onSort: PropTypes.func.isRequired,
    startIdx: PropTypes.number.isRequired,
    onReferralCountClick: PropTypes.func.isRequired,
};

// ============================================================
// Search bar (matches SearchProduct look; parent debounces onChange)
// ============================================================

export const AffiliateSearchBar = ({ value, onChange, placeholder }) => (
    <div className="filterContainer show searchBox mb-2">
        <div className="searchInputBox">
            <input
                value={value || ''}
                type="text"
                onChange={e => onChange(e.target.value)}
                onBlur={e => onChange(e.target.value.trim())}
                className="form-control searchInput"
                placeholder={placeholder}
                aria-label="Search invitations"
            />
        </div>
        <button
            type="button"
            disabled={!value}
            className="btn cus-primary-transparent-btn clrBtn"
            onClick={() => onChange('')}
        >
            Reset
        </button>
    </div>
);

AffiliateSearchBar.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
};

AffiliateSearchBar.defaultProps = {
    placeholder: 'Search by Company, Seller, Seller Code, STE Code, or Email',
};

// ============================================================
// CompanySeller filter panel (super-admin only) — mirrors the Inventory
// "Company + Seller" filter, with Apply / Clear buttons. `onApply` receives
// `{ companies, sellers }` (raw option arrays) so the container can serialise
// them into the `companyIds` / `sellerIds` arrays the invitations API now
// AND-combines server-side.
// ============================================================

export const CompanySellerMultiSelectFilter = ({ showFilter, initialCompanies, initialSellers, onApply, onClear }) => {
    // Primary dropdown data mirrors the Inventory filter: companies come from
    // /api/company-filter, sellers from /api/seller-filter, and dependent
    // sellers on company change come from /api/GetSellersByCompanyIds.
    const dispatch = useDispatch();
    const { filterCompanies, filterAllSellers } = useSelector(state => state.RateCardReducer);

    const [companyList, setCompanyList] = useState([]);
    const [sellerList, setSellerList] = useState([]);
    const [allSellers, setAllSellers] = useState([]);
    const [filterData, setFilterData] = useState({
        companyIdList: initialCompanies || [],
        sellerIdList: initialSellers || [],
    });

    useEffect(() => {
        dispatch(fetchCompanyFilters({ pageIndex: 1, pageSize: 10, sortColumn: 'name', sortOrder: 'ascending' }));
        dispatch(getFilterSellers({
            pageIndex: 1,
            pageSize: 10,
            sortColumn: 'name',
            sortOrder: 'ASCENDING',
            companyIdList: [0],
            screenType: 2,
        }));
    }, [dispatch]);

    useEffect(() => {
        const companies = filterCompanies?.Entity?.listCompanyMasterOrdersEntity || [];
        const sellers = filterAllSellers?.EntityList || [];
        if (companies.length) {
            setCompanyList(getSelectList(companies, 'Id', 'name'));
        }
        if (sellers.length) {
            const opts = getSelectList(sellers, 'id', 'sellerAndCompany');
            setAllSellers(opts);
            setSellerList(opts);
        }
    }, [filterCompanies, filterAllSellers]);

    const fetchSellersForCompanies = async (companyIds) => {
        if (!companyIds.length) {
            setSellerList(allSellers);
            return;
        }
        try {
            const res = await getAllSellersList({ companyIds });
            if (res?.data?.Status === 200) {
                const list = (res.data.Entity?.listSellerMasterOrdersEntity || []).map(item => ({
                    ...item,
                    label: item.sellerAndCompany,
                    value: item.id,
                }));
                setSellerList(list);
                setFilterData(prev => {
                    const merged = computeAutoSelectedSellers(list, companyIds, prev.sellerIdList || []);
                    if (merged.length === (prev.sellerIdList?.length || 0)) return prev;
                    return { ...prev, sellerIdList: merged };
                });
            }
        } catch {
            // Silent — dropdown just won't refresh.
        }
    };

    const handleMultiSelect = (value, fieldName) => {
        const nextValue = value || [];
        if (fieldName === 'companyIdList') {
            const companyIds = mapLodash(nextValue, 'Id');
            fetchSellersForCompanies(companyIds);
            // Drop any previously-selected seller whose company is no longer picked.
            const remainingSellers = (filterData.sellerIdList || []).filter(s =>
                companyIds.includes(parseInt(s.companyIds, 10)) // NOSONAR
            );
            setFilterData({ ...filterData, companyIdList: nextValue, sellerIdList: remainingSellers });
        } else {
            setFilterData({ ...filterData, [fieldName]: nextValue });
        }
    };

    const handleApply = () => {
        onApply({
            companies: filterData.companyIdList || [],
            sellers: filterData.sellerIdList || [],
        });
    };

    const handleReset = () => {
        setFilterData({ companyIdList: [], sellerIdList: [] });
        setSellerList(allSellers);
        onClear();
    };

    return (
        <div className={showFilter ? 'filterWrapperGradient filterContainer filterContainerZIndex show historyFilter zIndex2 pt-2' : 'filterContainer historyFilter'}>
            <div className="decorativeElem"></div>
            <div className="row mb10">
                <div className="col-sm-12 col-lg-4 mobMb-10">
                    <div className="row">
                        <div className="col-12">
                            <div className="labelHead">Company</div>
                        </div>
                        <div className="col-12 fontFix">
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                options={companyList}
                                classNamePrefix="list"
                                id="companyIdList"
                                name="companyIdList"
                                value={filterData.companyIdList}
                                onChange={(value) => handleMultiSelect(value, 'companyIdList')}
                                placeholder="Select Company..."
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-sm-12 col-lg-4 mobMb-10">
                    <div className="row">
                        <div className="col-12">
                            <div className="labelHead">Seller</div>
                        </div>
                        <div className="col-12 fontFix">
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                options={sellerList}
                                classNamePrefix="list"
                                id="sellerIdList"
                                name="sellerIdList"
                                value={filterData.sellerIdList}
                                onChange={(value) => handleMultiSelect(value, 'sellerIdList')}
                                placeholder="Select Seller..."
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-lg-4 col-sm-12">
                    <div className="row">
                        <div className="col-12">
                            <div className="hidLbl">-</div>
                        </div>
                        <div className="col-12 flexEnd">
                            <button
                                type="button"
                                className="btn cus-primary-transparent-btn top-btn"
                                onClick={handleReset}
                            >
                                Clear Filter
                            </button>
                            <button
                                type="button"
                                className="cus-seconday-bg-btn btn filterBtn"
                                onClick={handleApply}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

CompanySellerMultiSelectFilter.propTypes = {
    showFilter: PropTypes.bool,
    initialCompanies: PropTypes.arrayOf(PropTypes.object),
    initialSellers: PropTypes.arrayOf(PropTypes.object),
    onApply: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
};

// ============================================================
// Pagination Section
// ============================================================

export const AffiliatePaginationSection = ({ paginatedData, pagination, affiliateDataLength }) => {
    if (!paginatedData?.length) return null;

    return (
        <BatchPagination
            gridData={paginatedData}
            numberOfPage={pagination.numberOfPage}
            numOfPage={numOfPage}
            loader={false}
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            setCurrentPage={pagination.setCurrentPage}
            totalRecords={affiliateDataLength}
            setNumberOfPage={pagination.setNumberOfPage}
        />
    );
};

AffiliatePaginationSection.propTypes = {
    paginatedData: PropTypes.arrayOf(PropTypes.object).isRequired,
    pagination: PropTypes.shape({
        numberOfPage: PropTypes.number.isRequired,
        currentPage: PropTypes.number.isRequired,
        totalPages: PropTypes.number.isRequired,
        setCurrentPage: PropTypes.func.isRequired,
        setNumberOfPage: PropTypes.func.isRequired,
    }).isRequired,
    affiliateDataLength: PropTypes.number.isRequired,
};
